import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import instance from "../utils/api_instance.util";
import { errorToastify, successToastify } from "../utils/toastify.util";

const initialState = {
  step: 1, // 1=email, 2=otp
  email: "",
  userId: null,
  resetToken: null,
  isLoading: false,
  otpVerified: false,
};

/**
 * Step 1: Send OTP
 */
export const sendForgotOtp = createAsyncThunk(
  "forgot/sendOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const { data } = await instance.post("/api/v1/app/auth/forget-password", {
        email,
      });
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || { message: "Send OTP failed" },
      );
    }
  },
);

/**
 * Step 2: Verify OTP
 * Payload: { userId, email, otp }
 */
export const verifyForgotOtp = createAsyncThunk(
  "forgot/verifyOtp",
  async ({ userId, email, otp }, { rejectWithValue }) => {
    try {
      const { data } = await instance.post(
        "/api/v1/app/auth/verify-otp",
        { userId, email, otp },
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || { message: "Verify OTP failed" },
      );
    }
  },
);

/**
 * Step 3: Reset Password
 * Payload: { userId, password, resetToken }
 */
export const resetForgotPassword = createAsyncThunk(
  "forgot/resetPassword",
  async ({ userId, password, resetToken }, { rejectWithValue }) => {
    try {
      const { data } = await instance.post(
        "/api/v1/app/auth/reset-password",
        { userId, password, resetToken },
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || { message: "Reset password failed" },
      );
    }
  },
);

const forgotPasswordSlice = createSlice({
  name: "forgotPassword",
  initialState,
  reducers: {
    setStep: (state, action) => {
      state.step = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    resetForgot: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // SEND OTP
      .addCase(sendForgotOtp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendForgotOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload;

        if (data?.success === true) {
          successToastify(data?.mesasge || "OTP sent");
          state.userId = data?.result?.id ?? null;
          state.step = 2;
          state.otpVerified = false;
        } else {
          errorToastify(data?.mesasge || data?.message || "Failed to send OTP");
        }
      })
      .addCase(sendForgotOtp.rejected, (state, action) => {
        state.isLoading = false;
        errorToastify(
          action?.payload?.mesasge ||
            action?.payload?.message ||
            "Failed to send OTP",
        );
      })

      // VERIFY OTP
      .addCase(verifyForgotOtp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyForgotOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload;

        if (data?.success === true) {
          successToastify(data?.mesasge || "OTP verified");
          state.otpVerified = true;
          state.userId = data?.result?.id ?? state.userId;
          state.resetToken = data?.result?.resetToken ?? null;
        } else {
          errorToastify(data?.mesasge || data?.message || "Invalid OTP");
          state.otpVerified = false;
        }
      })
      .addCase(verifyForgotOtp.rejected, (state, action) => {
        state.isLoading = false;
        errorToastify(
          action?.payload?.mesasge ||
            action?.payload?.message ||
            "OTP verification failed",
        );
      })

      // RESET PASSWORD
      .addCase(resetForgotPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetForgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload;

        if (data?.success === true) {
          successToastify(data?.mesasge || "Password updated");
        } else {
          errorToastify(
            data?.mesasge || data?.message || "Failed to update password",
          );
        }
      })
      .addCase(resetForgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        errorToastify(
          action?.payload?.mesasge ||
            action?.payload?.message ||
            "Reset password failed",
        );
      });
  },
});

export const { setStep, setEmail, resetForgot } = forgotPasswordSlice.actions;
export default forgotPasswordSlice.reducer;
